// src/pages/TaxRatesPage.js
import React, { useEffect, useState } from 'react';
import api from '../api/axios';

import toast from 'react-hot-toast';

const TaxRatesPage = () => {
  const [rates, setRates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formValues, setFormValues] = useState({ name: '', rate: '' });
  const [formErrors, setFormErrors] = useState({});
  const [formSuccess, setFormSuccess] = useState('');

  const fetchRates = async () => {
    try {
      const res = await api.get('/taxrates');
      setRates(res.data);
    } catch (err) {
      toast.error('Failed to load tax rates');
    }
  };

  useEffect(() => {
    fetchRates();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tax rate?')) return;

    try {
      await api.delete(`/taxrates/${id}`);
      toast.success('Tax rate deleted');
      fetchRates();
    } catch (err) {
      toast.error('Error deleting tax rate');
    }
  };

  const startEdit = (rate) => {
    setEditingId(rate.id);
    setFormValues({
      name: rate.name,
      rate: (parseFloat(rate.rate) * 100).toFixed(2),
    });
    setFormErrors({});
    setFormSuccess('');
  };

  const validateForm = () => {
    const errors = {};
    const name = formValues.name.trim();
    const rate = formValues.rate.trim();

    if (!name) {
      errors.name = 'Tax name is required.';
    } else if (name.length > 120) {
      errors.name = 'Tax name should be 120 characters or fewer.';
    }

    if (!rate) {
      errors.rate = 'Rate is required.';
    } else {
      const parsed = Number(rate);
      if (Number.isNaN(parsed)) {
        errors.rate = 'Rate must be numeric.';
      } else if (parsed < 0) {
        errors.rate = 'Rate cannot be negative.';
      } else if (parsed > 100) {
        errors.rate = 'Rate cannot exceed 100%.';
      }
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    const payload = {
      name: formValues.name.trim(),
      rate: Number(formValues.rate) / 100,
    };

    try {
      if (editingId) {
        await api.put(`/taxrates/${editingId}`, payload);
        toast.success('Tax rate updated');
        setFormSuccess('Tax rate updated.');
      } else {
        await api.post('/taxrates', payload);
        toast.success('Tax rate added');
        setFormSuccess('Tax rate added.');
      }

      setFormValues({ name: '', rate: '' });
      setEditingId(null);
      setFormErrors({});
      fetchRates();
      setTimeout(() => setFormSuccess(''), 2500);
    } catch (err) {
      const message = err.response?.data?.error || 'Error saving tax rate';
      toast.error(message);
      setFormErrors({ form: message });
    }
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-6 overflow-auto bg-slate-950 px-6 py-6 text-slate-100">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-100">Manage Tax Rates</h1>

      <div className="grid gap-6 lg:grid-cols-[1.1fr,1.4fr]">
        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <header className="mb-4 space-y-1">
            <h2 className="text-lg font-semibold text-slate-100">
              {editingId ? 'Update tax rate' : 'Create tax rate'}
            </h2>
            <p className="text-sm text-slate-400">
              Define tax percentages that can be attached to products or applied at the till.
            </p>
          </header>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Tax name
              </label>
              <input
                type="text"
                value={formValues.name}
                onChange={(event) => {
                  setFormValues((prev) => ({ ...prev, name: event.target.value }));
                  setFormErrors((prev) => ({ ...prev, name: '' }));
                }}
                className={`w-full rounded-2xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brandGreen/30 ${
                  formErrors.name
                    ? 'border-rose-500/60 bg-rose-950/40 text-rose-100 placeholder:text-rose-300/60'
                    : 'border-slate-800 bg-slate-950/70 text-slate-100 placeholder:text-slate-500'
                }`}
                placeholder="e.g. VAT 7.5%"
                maxLength={160}
              />
              {formErrors.name && <p className="text-xs text-rose-300">{formErrors.name}</p>}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formValues.rate}
                onChange={(event) => {
                  setFormValues((prev) => ({ ...prev, rate: event.target.value }));
                  setFormErrors((prev) => ({ ...prev, rate: '' }));
                }}
                className={`w-full rounded-2xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brandGreen/30 ${
                  formErrors.rate
                    ? 'border-rose-500/60 bg-rose-950/40 text-rose-100 placeholder:text-rose-300/60'
                    : 'border-slate-800 bg-slate-950/70 text-slate-100 placeholder:text-slate-500'
                }`}
                placeholder="e.g. 7.50"
              />
              {formErrors.rate && <p className="text-xs text-rose-300">{formErrors.rate}</p>}
            </div>

            {formErrors.form && (
              <div className="rounded-2xl border border-rose-500/40 bg-rose-900/20 px-4 py-3 text-xs text-rose-200">
                {formErrors.form}
              </div>
            )}
            {formSuccess && !formErrors.form && (
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-900/20 px-4 py-3 text-xs text-emerald-200">
                {formSuccess}
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-full bg-brandGreen px-5 py-2 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-brandDark"
              >
                {editingId ? 'Update tax rate' : 'Add tax rate'}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingId(null);
                    setFormValues({ name: '', rate: '' });
                    setFormErrors({});
                    setFormSuccess('');
                  }}
                  className="inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-2 text-sm font-semibold uppercase tracking-wide text-slate-200 transition hover:border-slate-500 hover:text-white"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 shadow-[0_25px_60px_-30px_rgba(8,15,26,0.9)]">
          <header className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-100">Current tax rates</h2>
            <span className="text-xs text-slate-500">{rates.length} total</span>
          </header>

          <ul className="divide-y divide-slate-800">
            {rates.map((rate) => (
              <li
                key={rate.id}
                className="flex items-center justify-between py-3 text-sm text-slate-200"
              >
                <div>
                  <div className="font-medium text-slate-100">{rate.name}</div>
                  <div className="text-xs text-slate-500">
                    {(parseFloat(rate.rate) * 100).toFixed(2)}%
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs uppercase tracking-wide">
                  <button
                    onClick={() => startEdit(rate)}
                    className="rounded-full border border-brandGreen/50 px-3 py-1 text-brandGreen transition hover:bg-brandGreen/10"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(rate.id)}
                    className="rounded-full border border-rose-500/50 px-3 py-1 text-rose-300 transition hover:bg-rose-500/10"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
            {rates.length === 0 && (
              <li className="py-6 text-center text-xs text-slate-500">
                No tax rates defined yet. Create a rate to make it selectable on products.
              </li>
            )}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default TaxRatesPage;
